/**
 * useLocalStorage — a state hook backed by `localStorage`.
 *
 * Used by: theme choice, sidebar collapsed state, saved list filters, the
 * remembered tenant code on the login screen, and the POS device mode.
 *
 * NEVER put a secret in here. Access tokens live in memory only; the refresh
 * token's storage tradeoff is documented at its single call site in the Admin
 * app's `tokenStorage.ts`. `localStorage` is readable by any script on the
 * origin, so anything here should be safe to leak.
 *
 * Writes are wrapped in try/catch: Safari private mode and a full quota both
 * throw on `setItem`, and a settings write must never crash a POS mid-service.
 */
import { useCallback, useEffect, useState } from 'react';

export function useLocalStorage<T>(
  key: string,
  initialValue: T,
): [T, (value: T | ((previous: T) => T)) => void, () => void] {
  const read = useCallback((): T => {
    if (typeof window === 'undefined') return initialValue;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw === null) return initialValue;
      return JSON.parse(raw) as T;
    } catch {
      return initialValue;
    }
  }, [initialValue, key]);

  const [value, setValue] = useState<T>(read);

  const write = useCallback(
    (next: T | ((previous: T) => T)) => {
      setValue((previous) => {
        const resolved = typeof next === 'function' ? (next as (p: T) => T)(previous) : next;
        try {
          window.localStorage.setItem(key, JSON.stringify(resolved));
        } catch {
          // Quota exceeded or storage disabled — keep the in-memory value.
        }
        return resolved;
      });
    },
    [key],
  );

  const remove = useCallback(() => {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // ignore
    }
    setValue(initialValue);
  }, [initialValue, key]);

  // Keep two tabs of the same app in sync (a manager with Admin open twice).
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onStorage = (event: StorageEvent): void => {
      if (event.key !== key) return;
      setValue(read());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [key, read]);

  return [value, write, remove];
}
